/**
 * Client channel helpers:
 * - Order mark: cart attributes client_channel → order / abandoned checkout
 * - Customer mark: reportRegChannelOnce → backend Tag / metafield (once per browser+customer)
 */
(function (global) {
  const root = global.OffhooApp || (global.OffhooApp = {});
  const ATTR_CHANNEL = 'client_channel';
  const ATTR_AT = 'client_channel_at';
  const VALID = { app: true, ios: true, android: true, h5: true, pc: true };
  const REG_CHANNEL_TAG_PREFIX = 'reg_channel:';
  const CART_LINES_UPDATE = 'shopify:cart:lines-update';
  /** template.name → report source (whitelist pages only) */
  const REG_CHANNEL_PAGE_SOURCES = {
    index: 'home',
    collection: 'collection',
    product: 'product',
  };

  /** @type {string|null} */
  let lastSyncedChannel = null;
  /** @type {Promise<boolean>|null} */
  let inflight = null;
  /** @type {Promise<boolean>|null} */
  let regChannelInflight = null;
  let checkoutGuardBound = false;

    /**
     * @param {unknown} value
     * @returns {value is 'app'|'ios'|'android'|'h5'|'pc'}
     */
    function isValidChannel(value) {
        return typeof value === 'string' && Boolean(VALID[value]);
    }

    /**
     * 1) getAppPlatform → ios|android
     * 2) else isInApp → app
     * 3) else UA mobile → h5, otherwise pc
     * @returns {'app'|'ios'|'android'|'h5'|'pc'}
     */
    root.getClientChannel = function getClientChannel() {
        try {
            if (typeof root.getAppPlatform === 'function') {
                const platform = root.getAppPlatform();
                if (platform === 'IOS') return 'ios';
                if (platform === 'ANDROID') return 'android';
            }
        } catch (_e) {
            /* ignore */
        }

        try {
            if (typeof root.isInApp === 'function' && root.isInApp()) return 'app';
        } catch (_e) {
            /* ignore */
        }

        try {
            const ua = String(global.navigator?.userAgent || '');
            if (
                /Android|iPhone|iPad|iPod|Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
            ) {
                return 'h5';
            }
        } catch (_e) {
            /* ignore */
        }

        return 'pc';
    };

  /**
   * @returns {string}
   */
  function cartUpdateUrl() {
    const fromTheme = global.Theme?.routes?.cart_update_url;
    if (fromTheme) return fromTheme;
    return '/cart/update.js';
  }

  root.clearRegChannelMarked = function clearRegChannelMarked() {
    // No-op: dedupe is based on customer.tags (reg_channel:*), not localStorage.
  };

  /**
   * @param {unknown} tags
   * @returns {boolean}
   */
  function hasRegChannelTag(tags) {
    if (!Array.isArray(tags)) return false;
    return tags.some((tag) => {
      const text = String(tag || '')
        .trim()
        .toLowerCase();
      return text.indexOf(REG_CHANNEL_TAG_PREFIX) === 0;
    });
  }

  /**
   * Report registration channel once per customer when tags lack reg_channel:*.
   *
   * @param {{
   *   customerId?: string|number|null,
   *   apiUrl?: string|null,
   *   shop?: string|null,
   *   source?: string|null,
   *   customerTags?: unknown,
   * }} [options]
   * @returns {Promise<boolean>}
   */
  root.reportRegChannelOnce = function reportRegChannelOnce(options) {
    const opts = options || {};
    const themeOffhoo = (global.Theme && global.Theme.offhoo) || {};

    const apiUrl = String(opts.apiUrl || themeOffhoo.regChannelApiUrl || '').trim();
    if (!apiUrl) return Promise.resolve(false);

    const customerId = String(opts.customerId || themeOffhoo.customerId || '').trim();
    if (!customerId) return Promise.resolve(false);

    const tags =
      opts.customerTags !== undefined ? opts.customerTags : themeOffhoo.customerTags;
    if (hasRegChannelTag(tags)) {
      console.log(
        '[Offhoo][reg_channel] already has tag',
        String(opts.source || 'unknown'),
        (global.location && global.location.pathname) || '',
        customerId
      );
      return Promise.resolve(true);
    }

    const source = String(opts.source || 'unknown').trim() || 'unknown';
    console.log(
      '[Offhoo][reg_channel] will report',
      source,
      (global.location && global.location.pathname) || '',
      customerId
    );

    if (regChannelInflight) return regChannelInflight;

    let channel = 'h5';
    try {
      channel = root.getClientChannel();
    } catch (_e) {
      /* ignore */
    }
    if (!isValidChannel(channel)) channel = 'h5';

    const shop = String(opts.shop || themeOffhoo.shop || '').trim();
    const payload = {
      customer_id: customerId,
      channel,
      source,
      shop,
    };

    regChannelInflight = fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'omit',
      mode: 'cors',
    })
      .then((res) => {
        if (!res.ok) {
          console.warn('[Offhoo] reg_channel report failed', res.status, source);
          return false;
        }
        return true;
      })
      .catch((err) => {
        console.warn('[Offhoo] reg_channel report error', err);
        return false;
      })
      .finally(() => {
        regChannelInflight = null;
      });

    return regChannelInflight;
  };

  /**
   * Whitelist: index / collection / product when Liquid customer is present.
   */
  function maybeReportRegChannelForTemplate() {
    const cfg = global.OffhooAppConfig || {};
    const templateName = cfg.templateName || '';
    const source = REG_CHANNEL_PAGE_SOURCES[templateName];
    if (!source) return;

    const themeOffhoo = (global.Theme && global.Theme.offhoo) || {};
    if (!themeOffhoo.customerId) return;

    root.reportRegChannelOnce({ source });
  }

    /**
     * Write client_channel onto the cart (copied to the order at checkout).
     * @param {{ force?: boolean }} [options]
     * @returns {Promise<boolean>}
     */
    root.syncClientChannelToCart = function syncClientChannelToCart(options) {
        const force = Boolean(options && options.force);
        const channel = root.getClientChannel();

        if (!force && lastSyncedChannel === channel) {
            return Promise.resolve(true);
        }

        if (inflight) return inflight;

        const at = new Date().toISOString();
        const body = JSON.stringify({
            attributes: {
                [ATTR_CHANNEL]: channel,
                [ATTR_AT]: at,
            },
        });

        inflight = fetch(cartUpdateUrl(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body,
            credentials: 'same-origin',
        })
            .then((response) => {
                if (!response.ok) throw new Error(`cart update ${response.status}`);
                return response.json();
            })
            .then((cart) => {
                const written = cart && cart.attributes && cart.attributes[ATTR_CHANNEL];
                if (isValidChannel(written)) {
                    lastSyncedChannel = written;
                } else {
                    lastSyncedChannel = channel;
                }
                return true;
            })
            .catch((err) => {
                console.warn('[Offhoo] client_channel cart sync failed:', err);
                return false;
            })
            .finally(() => {
                inflight = null;
            });

        return inflight;
    };

    /**
     * @param {EventTarget|null} target
     * @returns {HTMLElement|null}
     */
    function checkoutTriggerFrom(target) {
        if (!(target instanceof Element)) return null;
        return target.closest(
            '[name="checkout"], [data-checkout-button], a[href*="/checkout"], button[href*="/checkout"]'
        );
    }

    function bindCheckoutGuard() {
        if (checkoutGuardBound) return;
        checkoutGuardBound = true;

        document.addEventListener(
            'click',
            (event) => {
                const trigger = checkoutTriggerFrom(event.target);
                if (!trigger) return;
                if (trigger.getAttribute('data-offhoo-channel-skip') === '1') return;

                const channel = root.getClientChannel();
                if (lastSyncedChannel === channel) return;

                event.preventDefault();
                event.stopPropagation();

                root.syncClientChannelToCart({ force: true }).finally(() => {
                    trigger.setAttribute('data-offhoo-channel-skip', '1');
                    try {
                        if (typeof trigger.click === 'function') {
                            trigger.click();
                        } else if (trigger instanceof HTMLAnchorElement && trigger.href) {
                            global.location.href = trigger.href;
                        }
                    } finally {
                        global.setTimeout(() => {
                            trigger.removeAttribute('data-offhoo-channel-skip');
                        }, 1500);
                    }
                });
            },
            true
        );

        document.addEventListener(
            'submit',
            (event) => {
                const form = event.target;
                if (!(form instanceof HTMLFormElement)) return;

                const submitter = event.submitter;
                const isCheckout =
                    (submitter && submitter.getAttribute && submitter.getAttribute('name') === 'checkout') ||
                    Boolean(form.querySelector('[name="checkout"]'));
                if (!isCheckout) return;
                if (form.getAttribute('data-offhoo-channel-skip') === '1') return;

                const channel = root.getClientChannel();
                if (lastSyncedChannel === channel) return;

                event.preventDefault();
                event.stopPropagation();

                root.syncClientChannelToCart({ force: true }).finally(() => {
                    form.setAttribute('data-offhoo-channel-skip', '1');
                    try {
                        if (typeof form.requestSubmit === 'function' && submitter instanceof HTMLElement) {
                            form.requestSubmit(submitter);
                        } else {
                            form.submit();
                        }
                    } finally {
                        global.setTimeout(() => {
                            form.removeAttribute('data-offhoo-channel-skip');
                        }, 1500);
                    }
                });
            },
            true
        );
    }

    function boot() {
        bindCheckoutGuard();
        root.syncClientChannelToCart();
        maybeReportRegChannelForTemplate();

        document.addEventListener(CART_LINES_UPDATE, () => {
            root.syncClientChannelToCart();
        });

        global.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                lastSyncedChannel = null;
                root.syncClientChannelToCart({ force: true });
                maybeReportRegChannelForTemplate();
            }
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
    } else {
        boot();
    }
})(window);
